import type { SessionUser } from "@/shared/types";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { GlobalState } from "./types";

const initialState: GlobalState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  isLoginPromptOpen: false,
};

export const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    /** Mirrors the Web3Auth context into redux. Called by `GlobalProvider`. */
    setSession: (
      state,
      action: PayloadAction<{
        user: SessionUser | null;
        isAuthenticated: boolean;
        isLoading: boolean;
        error: string | null;
      }>
    ) => {
      const next = action.payload.user;
      // The session lands in two steps — claims first, wallet address a tick
      // later — so `setSession` can fire again after `setIsAdmin` has already
      // resolved. Carry the server-resolved flag over for the same account
      // instead of resetting it to the pessimistic default.
      state.user = next
        ? {
            ...next,
            isAdmin:
              next.isAdmin ||
              (state.user?.email === next.email
                ? (state.user?.isAdmin ?? false)
                : false),
          }
        : null;
      state.isAuthenticated = action.payload.isAuthenticated;
      state.isLoading = action.payload.isLoading;
      state.error = action.payload.error;
    },
    /**
     * Admin is decided server-side (ADMIN_EMAILS). The admin container flips
     * this once the server has actually served it an admin response, so the
     * nav can show the ops link without the client ever asserting the role.
     */
    setIsAdmin: (state, action: PayloadAction<boolean>) => {
      if (state.user) state.user.isAdmin = action.payload;
    },
    setLoginPromptOpen: (state, action: PayloadAction<boolean>) => {
      state.isLoginPromptOpen = action.payload;
    },
    /** Triggers the logout saga. */
    logOut: (_state) => {},
    /** Wipes every slice — handled by the root reducer in store/reducers.ts. */
    resetState: (_state) => {},
  },
});

export const { actions: globalActions, reducer: globalReducer } = globalSlice;
